/**
 * DrivingLog (Körjournal)
 * Körjournal enligt Skatteverkets krav för skattemässig dokumentation.
 */
import { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { useTranslation } from '@/store/languageStore';
import {
  DocumentArrowDownIcon,
  PrinterIcon,
  ClipboardDocumentListIcon,
  CalendarIcon,
  ArrowLeftIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import api from '@/services/api/client';
import SearchableVehicleSelect from '@/components/vehicle/SearchableVehicleSelect';
import { ROUTES } from '@/constants/routes';

import DateInput from '@/components/common/DateInput';

interface DrivingLogEntry {
  tripId: string;
  date: string;
  startTime: string;
  endTime: string;
  startAddress: string;
  endAddress: string;
  startOdometer: number | null;
  endOdometer: number | null;
  distanceKm: number;
  durationMinutes: number;
  driverName: string;
  purpose: 'business' | 'private' | '';
  note: string | null;
  /** trips.trip_type — mätarjustering ska inte klassas som tjänst/privat */
  tripType?: string | null;
  submittedAt?: string | null;
  /** Summa tullavgift för resan (geofence enter med kostnad), från API */
  tollTotalSek?: number;
}

const DrivingLog = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const noteTimersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const autoFetchedRef = useRef(false);
  const queryVehicleId = searchParams.get('vehicleId') ?? '';
  const queryStart = searchParams.get('startDate');
  const queryEnd = searchParams.get('endDate');
  const isDate = (value: string | null): value is string => Boolean(value && /^\d{4}-\d{2}-\d{2}$/.test(value));

  const [selectedVehicleId, setSelectedVehicleId] = useState<string>(queryVehicleId);
  const [startDate, setStartDate] = useState<string>(isDate(queryStart) ? queryStart : format(startOfMonth(new Date()), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState<string>(isDate(queryEnd) ? queryEnd : format(endOfMonth(new Date()), 'yyyy-MM-dd'));
  const [entries, setEntries] = useState<DrivingLogEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isPeriodLocked, setIsPeriodLocked] = useState(false);
  const [updatingPurpose, setUpdatingPurpose] = useState<string | null>(null);

  const isAdjRow = (e: DrivingLogEntry) => e.tripType === 'adjustment';

  const totalTrips = entries.length;
  /** Endast faktisk körning — mätarjustering räknas separat (samma som backend). */
  const totalDistance = useMemo(
    () => entries.filter((e) => !isAdjRow(e)).reduce((s, e) => s + e.distanceKm, 0),
    [entries]
  );
  const privateDistanceKm = useMemo(
    () =>
      entries
        .filter((e) => !isAdjRow(e) && e.purpose === 'private')
        .reduce((s, e) => s + e.distanceKm, 0),
    [entries]
  );
  const businessDistanceKm = useMemo(
    () =>
      entries
        .filter((e) => !isAdjRow(e) && e.purpose === 'business')
        .reduce((s, e) => s + e.distanceKm, 0),
    [entries]
  );
  const undefinedDistanceKm = useMemo(
    () =>
      entries
        .filter((e) => !isAdjRow(e) && e.purpose !== 'private' && e.purpose !== 'business')
        .reduce((s, e) => s + e.distanceKm, 0),
    [entries]
  );
  const adjustmentDistanceKm = useMemo(
    () => entries.filter((e) => isAdjRow(e)).reduce((s, e) => s + e.distanceKm, 0),
    [entries]
  );

  const privateTollSek = useMemo(
    () =>
      entries
        .filter((e) => !isAdjRow(e) && e.purpose === 'private')
        .reduce((s, e) => s + (Number(e.tollTotalSek) || 0), 0),
    [entries]
  );
  const businessTollSek = useMemo(
    () =>
      entries
        .filter((e) => !isAdjRow(e) && e.purpose === 'business')
        .reduce((s, e) => s + (Number(e.tollTotalSek) || 0), 0),
    [entries]
  );
  const undefinedTollSek = useMemo(
    () =>
      entries
        .filter((e) => !isAdjRow(e) && e.purpose !== 'private' && e.purpose !== 'business')
        .reduce((s, e) => s + (Number(e.tollTotalSek) || 0), 0),
    [entries]
  );

  const formatTollSek = (sek: number | undefined) => {
    const n = Number(sek);
    if (!Number.isFinite(n) || n === 0) return '–';
    return new Intl.NumberFormat('sv-SE', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(n);
  };

  const formatTollSekAlways = (sek: number) => {
    const n = Number(sek);
    if (!Number.isFinite(n)) return '0';
    return new Intl.NumberFormat('sv-SE', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(n);
  };

  const fetchDrivingLog = useCallback(async () => {
    if (!selectedVehicleId) {
      toast.error(t('reports.drivingLog.selectVehicle'));
      return;
    }
    setIsLoading(true);
    try {
      const response = await api.get('/api/reports/driving-log', {
        params: { vehicleId: selectedVehicleId, startDate, endDate },
      });
      if (response.data.success) {
        const data = response.data.data;
        setEntries(data.entries || []);
        setIsPeriodLocked(Boolean(data.isPeriodLocked));
      } else {
        toast.error(response.data.error || t('reports.drivingLog.fetchError'));
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || t('reports.drivingLog.fetchError'));
    } finally {
      setIsLoading(false);
    }
  }, [selectedVehicleId, startDate, endDate, t]);

  useEffect(() => {
    if (autoFetchedRef.current || !queryVehicleId || selectedVehicleId !== queryVehicleId) return;
    autoFetchedRef.current = true;
    void fetchDrivingLog();
  }, [fetchDrivingLog, queryVehicleId, selectedVehicleId]);

  const updatePurpose = async (tripId: string, newPurpose: 'business' | 'private' | null) => {
    if (isPeriodLocked) return;
    const prevPurpose =
      entries.find((e) => e.tripId === tripId)?.purpose ?? ('' as DrivingLogEntry['purpose']);
    const nextPurpose = (newPurpose ?? '') as DrivingLogEntry['purpose'];
    setEntries((prev) => prev.map((e) => (e.tripId === tripId ? { ...e, purpose: nextPurpose } : e)));
    setUpdatingPurpose(tripId);
    try {
      await api.patch(`/api/reports/driving-log/${tripId}/purpose`, { purpose: newPurpose });
    } catch {
      setEntries((prev) => prev.map((e) => (e.tripId === tripId ? { ...e, purpose: prevPurpose } : e)));
      toast.error(t('reports.drivingLog.purposeUpdateError'));
    } finally {
      setUpdatingPurpose(null);
    }
  };

  const flushNote = useCallback(
    async (tripId: string, note: string) => {
      if (isPeriodLocked) return;
      try {
        await api.patch(`/api/reports/driving-log/${tripId}/note`, { note: note.trim() || null });
      } catch {
        toast.error(t('reports.drivingLog.noteUpdateError'));
      }
    },
    [isPeriodLocked, t]
  );

  const scheduleNoteSave = (tripId: string, note: string) => {
    if (isPeriodLocked) return;
    const prev = noteTimersRef.current[tripId];
    if (prev) clearTimeout(prev);
    noteTimersRef.current[tripId] = setTimeout(() => {
      void flushNote(tripId, note);
      delete noteTimersRef.current[tripId];
    }, 500);
  };

  const handleSubmitPeriod = async () => {
    if (!selectedVehicleId) {
      toast.error(t('reports.drivingLog.selectVehicle'));
      return;
    }
    if (!entries.length) {
      toast.error(t('reports.drivingLog.noDataToSubmit'));
      return;
    }
    if (isPeriodLocked) return;
    const ok = window.confirm(t('reports.drivingLog.submitConfirm'));
    if (!ok) return;
    setSubmitting(true);
    try {
      const res = await api.post('/api/reports/driving-log/submit', {
        vehicleId: parseInt(selectedVehicleId, 10),
        startDate,
        endDate,
      });
      if (res.data.success) {
        toast.success(res.data.message || t('reports.drivingLog.submitSuccess'));
        await fetchDrivingLog();
      } else {
        toast.error(res.data.error || t('common.error'));
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || t('reports.drivingLog.submitError'));
    } finally {
      setSubmitting(false);
    }
  };

  const exportToCsv = () => {
    if (entries.length === 0) {
      toast.error(t('reports.drivingLog.noExportData'));
      return;
    }
    const headers = [
      t('copy.reports.date'),
      t('copy.reports.startTime'),
      t('copy.reports.endTime'),
      t('copy.reports.fromAddress'),
      t('copy.reports.toAddress'),
      t('copy.reports.odometerStartKm'),
      t('copy.reports.odometerEndKm'),
      t('copy.reports.distanceDrivenKm'),
      t('copy.reports.travelTimeMin'),
      t('reports.drivingLog.colToll'),
      t('copy.reports.driver'),
      t('reports.drivingLog.csvColType'),
      t('reports.drivingLog.csvColPurposeShort'),
      t('reports.drivingLog.csvColNote'),
    ];
    const rows = entries.map((entry) => [
      entry.date,
      entry.startTime,
      entry.endTime,
      `"${(entry.startAddress || '').replace(/"/g, '""')}"`,
      `"${(entry.endAddress || '').replace(/"/g, '""')}"`,
      entry.startOdometer !== null ? entry.startOdometer.toFixed(0) : '',
      entry.endOdometer !== null ? entry.endOdometer.toFixed(0) : '',
      entry.distanceKm.toFixed(1),
      entry.durationMinutes.toString(),
      (Number(entry.tollTotalSek) || 0).toFixed(2),
      `"${(entry.driverName || '').replace(/"/g, '""')}"`,
      entry.purpose === 'business'
        ? t('reports.drivingLog.business')
        : entry.purpose === 'private'
          ? t('reports.drivingLog.private')
          : isAdjRow(entry)
            ? t('reports.drivingLog.correctionPurposeLabel')
            : t('reports.drivingLog.undefined'),
      entry.purpose === 'business' ? 'T' : entry.purpose === 'private' ? 'P' : isAdjRow(entry) ? '-' : '?',
      `"${(entry.note || '').replace(/"/g, '""')}"`,
    ]);
    const summaryBlock = [
      t('reports.drivingLog.csvSummaryHeading'),
      [
        t('reports.drivingLog.totalDistanceLabel'),
        '',
        '',
        '',
        '',
        '',
        '',
        totalDistance.toFixed(1) + ' km',
      ].join(';'),
      [
        t('reports.drivingLog.privateDistanceLabel'),
        '',
        '',
        '',
        '',
        '',
        '',
        privateDistanceKm.toFixed(1) + ' km',
      ].join(';'),
      [
        t('reports.drivingLog.businessDistanceLabel'),
        '',
        '',
        '',
        '',
        '',
        '',
        businessDistanceKm.toFixed(1) + ' km',
      ].join(';'),
      [
        t('reports.drivingLog.undefinedDistanceLabel'),
        '',
        '',
        '',
        '',
        '',
        '',
        undefinedDistanceKm.toFixed(1) + ' km',
      ].join(';'),
      ...(adjustmentDistanceKm > 0
        ? [
            [
              t('reports.drivingLog.adjustmentDistanceLabel'),
              '',
              '',
              '',
              '',
              '',
              '',
              adjustmentDistanceKm.toFixed(1) + ' km',
            ].join(';'),
          ]
        : []),
      ...(businessTollSek + privateTollSek + undefinedTollSek > 0
        ? [
            [t('reports.drivingLog.csvTollBusinessLabel'), '', '', '', '', '', '', `${businessTollSek.toFixed(2)} SEK`].join(
              ';'
            ),
            [t('reports.drivingLog.csvTollPrivateLabel'), '', '', '', '', '', '', `${privateTollSek.toFixed(2)} SEK`].join(
              ';'
            ),
            ...(undefinedTollSek > 0
              ? [
                  [
                    t('reports.drivingLog.csvTollUndefinedLabel'),
                    '',
                    '',
                    '',
                    '',
                    '',
                    '',
                    `${undefinedTollSek.toFixed(2)} SEK`,
                  ].join(';'),
                ]
              : []),
          ]
        : []),
      [t('reports.drivingLog.tripCountLabel'), '', '', '', '', '', '', totalTrips.toString()].join(';'),
      '',
    ].join('\n');
    const csvContent = [
      t('reports.drivingLog.title'),
      `${t('reports.drivingLog.periodLabel')}: ${startDate} - ${endDate}`,
      '',
      summaryBlock,
      headers.join(';'),
      ...rows.map((row) => row.join(';')),
    ].join('\n');
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const fileName = `${t('copy.reports.drivingLogFilePrefix')}_${selectedVehicleId || t('copy.reports.vehicleFileToken')}_${startDate}_${endDate}.csv`;
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(link.href);
    toast.success(`${t('reports.drivingLog.exportSuccess')}: ${fileName}`);
  };

  const handlePrint = () => {
    window.print();
  };

  const formatDuration = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}min`;
    }
    return `${mins}min`;
  };

  const purposeLabel = (p: DrivingLogEntry['purpose']) => {
    if (p === 'business') return t('reports.drivingLog.business');
    if (p === 'private') return t('reports.drivingLog.private');
    return t('reports.drivingLog.undefined');
  };

  const purposeLabelForRow = (e: DrivingLogEntry) =>
    isAdjRow(e) ? t('reports.drivingLog.correctionPurposeLabel') : purposeLabel(e.purpose);

  const rowLocked = (e: DrivingLogEntry) => isPeriodLocked || Boolean(e.submittedAt);

  return (
    <div className="p-6 max-w-[110rem] mx-auto w-full min-w-0">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <button
            type="button"
            onClick={() => navigate(ROUTES.SYSTEM_REPORTS)}
            className="p-2 hover:bg-bgElevated rounded-lg transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5 text-textDefault" />
          </button>
          <h1 className="text-2xl font-bold text-textDefault flex items-center gap-2">
            <ClipboardDocumentListIcon className="w-6 h-6 text-primary" />
            {t('reports.drivingLog.title')}
          </h1>
        </div>
        <p className="text-textSecondary">{t('reports.drivingLog.description')}</p>
      </div>

      <div className="bg-bgElevated shadow rounded-lg p-6 mb-6">
        <h2 className="text-lg font-semibold text-textDefault mb-4">{t('reports.drivingLog.filtersTitle')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-textDefault mb-2">{t('reports.drivingLog.vehicleLabel')}</label>
            <SearchableVehicleSelect
              selectedValue={selectedVehicleId}
              onChange={setSelectedVehicleId}
              placeholder={t('drivingLog.searchVehiclePlaceholder')}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-textDefault mb-2">
              <CalendarIcon className="w-4 h-4 inline mr-1" />
              {t('reports.drivingLog.fromDate')}
            </label>
            <DateInput value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-md shadow-sm bg-bgBase text-textDefault focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-textDefault mb-2">{t('reports.drivingLog.toDate')}</label>
            <DateInput value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-md shadow-sm bg-bgBase text-textDefault focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
            />
          </div>
        </div>
        <div className="flex flex-wrap gap-3 mt-4">
          <button
            type="button"
            onClick={fetchDrivingLog}
            disabled={isLoading || !selectedVehicleId}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                {t('common.loading')}
              </>
            ) : (
              <>
                <ClipboardDocumentListIcon className="w-4 h-4" />
                {t('reports.drivingLog.fetch')}
              </>
            )}
          </button>
          <button
            type="button"
            onClick={exportToCsv}
            disabled={entries.length === 0}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <DocumentArrowDownIcon className="w-4 h-4" />
            {t('common.exportCsv')}
          </button>
          <button
            type="button"
            onClick={handlePrint}
            disabled={entries.length === 0}
            className="px-4 py-2 bg-slate-700 text-white border border-slate-800 rounded-lg hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 print:hidden"
          >
            <PrinterIcon className="w-4 h-4" />
            {t('reports.drivingLog.print')}
          </button>
          <button
            type="button"
            onClick={handleSubmitPeriod}
            disabled={submitting || entries.length === 0 || isPeriodLocked}
            className="px-4 py-2 bg-emerald-700 text-white rounded-lg hover:bg-emerald-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 print:hidden"
          >
            <PaperAirplaneIcon className="w-4 h-4" />
            {submitting ? t('reports.drivingLog.submitting') : t('reports.drivingLog.submit')}
          </button>
        </div>
        {isPeriodLocked && entries.length > 0 && (
          <p className="mt-3 text-sm text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded-md px-3 py-2">
            {t('reports.drivingLog.periodLockedHint')}
          </p>
        )}
      </div>

      {entries.length > 0 ? (
        <div className="driving-log-print-root space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-4">
            <div className="bg-bgElevated rounded-lg border border-border p-4">
              <div className="text-sm text-textSecondary">{t('reports.drivingLog.tripCountShort')}</div>
              <div className="text-xl font-bold text-blue-600">{totalTrips}</div>
            </div>
            <div className="bg-bgElevated rounded-lg border border-border p-4">
              <div className="text-sm text-textSecondary">{t('reports.drivingLog.totalDistanceShort')}</div>
              <div className="text-xl font-bold text-blue-600">{totalDistance.toFixed(1)} km</div>
            </div>
            <div className="bg-bgElevated rounded-lg border border-border p-4">
              <div className="text-sm text-textSecondary">{t('reports.drivingLog.privateDistanceShort')}</div>
              <div className="text-xl font-bold text-green-600">{privateDistanceKm.toFixed(1)} km</div>
            </div>
            <div className="bg-bgElevated rounded-lg border border-border p-4">
              <div className="text-sm text-textSecondary">{t('reports.drivingLog.businessDistanceShort')}</div>
              <div className="text-xl font-bold text-red-600">{businessDistanceKm.toFixed(1)} km</div>
            </div>
            <div className="bg-bgElevated rounded-lg border border-border p-4">
              <div className="text-sm text-textSecondary">{t('reports.drivingLog.undefinedDistanceShort')}</div>
              <div className="text-xl font-bold text-amber-700">{undefinedDistanceKm.toFixed(1)} km</div>
            </div>
            <div className="bg-bgElevated rounded-lg border border-border p-4">
              <div className="text-sm text-textSecondary">{t('reports.drivingLog.adjustmentDistanceShort')}</div>
              <div className="text-xl font-bold text-slate-600 dark:text-slate-300">
                {adjustmentDistanceKm.toFixed(1)} km
              </div>
            </div>
            <div className="bg-bgElevated rounded-lg border border-border p-4 xl:col-span-1">
              <div className="text-sm text-textSecondary">{t('reports.drivingLog.tollSummaryTitle')}</div>
              <div className="mt-2 space-y-1 text-sm">
                <div className="flex justify-between gap-2">
                  <span className="text-textSecondary">{t('reports.drivingLog.privateDistanceShort')}</span>
                  <span className="font-semibold text-green-600 tabular-nums">
                    {formatTollSekAlways(privateTollSek)} SEK
                  </span>
                </div>
                <div className="flex justify-between gap-2">
                  <span className="text-textSecondary">{t('reports.drivingLog.businessDistanceShort')}</span>
                  <span className="font-semibold text-red-600 tabular-nums">
                    {formatTollSekAlways(businessTollSek)} SEK
                  </span>
                </div>
                {undefinedTollSek > 0 ? (
                  <div className="flex justify-between gap-2">
                    <span className="text-textSecondary">{t('reports.drivingLog.undefinedDistanceShort')}</span>
                    <span className="font-semibold text-amber-700 tabular-nums">
                      {formatTollSekAlways(undefinedTollSek)} SEK
                    </span>
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          <div className="bg-bgElevated rounded-lg border border-border overflow-hidden print:border-black">
            <div className="hidden print:block p-4 border-b border-border print-summary-header">
              <h2 className="text-xl font-bold">{t('reports.drivingLog.title')}</h2>
              <p className="text-sm">
                {t('reports.drivingLog.periodLabel')}: {startDate} - {endDate}
              </p>
            </div>
            <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[1280px]">
              <thead className="bg-bgContainer border-b border-border">
                <tr>
                  <th className="px-2 py-2 text-left font-medium text-textSecondary w-24 whitespace-nowrap">
                    {t('reports.drivingLog.colDate')}
                  </th>
                  <th className="px-2 py-2 text-left font-medium text-textSecondary w-28 whitespace-nowrap">
                    {t('reports.drivingLog.colTime')}
                  </th>
                  <th className="px-2 py-2 text-left font-medium text-textSecondary min-w-[120px]">{t('reports.drivingLog.colFrom')}</th>
                  <th className="px-2 py-2 text-left font-medium text-textSecondary min-w-[120px]">{t('reports.drivingLog.colTo')}</th>
                  <th className="px-2 py-2 text-right font-medium text-textSecondary w-24 whitespace-nowrap">
                    {t('reports.drivingLog.colOdoStart')}
                  </th>
                  <th className="px-2 py-2 text-right font-medium text-textSecondary w-24 whitespace-nowrap">
                    {t('reports.drivingLog.colOdoEnd')}
                  </th>
                  <th className="px-2 py-2 text-right font-medium text-textSecondary w-20 whitespace-nowrap">
                    {t('reports.drivingLog.colDistance')}
                  </th>
                  <th className="px-2 py-2 text-right font-medium text-textSecondary w-20 whitespace-nowrap">
                    {t('reports.drivingLog.colDuration')}
                  </th>
                  <th className="px-2 py-2 text-right font-medium text-textSecondary w-24 whitespace-nowrap">
                    {t('reports.drivingLog.colToll')}
                  </th>
                  <th className="px-2 py-2 text-center font-medium text-textSecondary w-28 whitespace-nowrap">
                    {t('reports.drivingLog.colTripType')}
                  </th>
                  <th className="px-2 py-2 text-center font-medium text-textSecondary w-28 whitespace-nowrap">
                    {t('reports.drivingLog.colPurpose')}
                  </th>
                  <th className="px-2 py-2 text-left font-medium text-textSecondary min-w-[280px]">{t('reports.drivingLog.colNote')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {entries.map((entry) => (
                  <tr key={entry.tripId} className="hover:bg-bgBase/50">
                    <td className="px-2 py-2 text-textPrimary text-xs whitespace-nowrap">{entry.date}</td>
                    <td className="px-2 py-2 text-textSecondary text-xs whitespace-nowrap">
                      {entry.startTime} → {entry.endTime}
                    </td>
                    <td className="px-2 py-2 text-textPrimary text-xs truncate max-w-[180px]" title={entry.startAddress}>
                      {entry.startAddress || '-'}
                    </td>
                    <td className="px-2 py-2 text-textPrimary text-xs truncate max-w-[180px]" title={entry.endAddress}>
                      {entry.endAddress || '-'}
                    </td>
                    <td className="px-2 py-2 text-right text-textSecondary text-xs whitespace-nowrap">
                      {entry.startOdometer !== null ? entry.startOdometer.toLocaleString('sv-SE') : '-'}
                    </td>
                    <td className="px-2 py-2 text-right text-textSecondary text-xs whitespace-nowrap">
                      {entry.endOdometer !== null ? entry.endOdometer.toLocaleString('sv-SE') : '-'}
                    </td>
                    <td className="px-2 py-2 text-right font-medium text-blue-600 text-xs whitespace-nowrap">
                      {entry.distanceKm.toFixed(1)} km
                    </td>
                    <td className="px-2 py-2 text-right text-textSecondary text-xs whitespace-nowrap">
                      {formatDuration(entry.durationMinutes)}
                    </td>
                    <td
                      className="px-2 py-2 text-right text-textSecondary text-xs whitespace-nowrap tabular-nums"
                      title={t('reports.drivingLog.tollFollowsTripPurposeHint')}
                    >
                      {formatTollSek(entry.tollTotalSek)}
                    </td>
                    <td
                      className={`px-2 py-2 text-center text-sm font-medium whitespace-nowrap ${
                        entry.purpose === 'business'
                          ? 'text-red-600'
                          : entry.purpose === 'private'
                            ? 'text-green-600'
                            : 'text-amber-800'
                      }`}
                    >
                      {purposeLabelForRow(entry)}
                    </td>
                    <td className="px-2 py-2 text-center">
                      <div className="flex items-center justify-center">
                        <button
                          type="button"
                          onClick={() => {
                            const next = entry.purpose === 'private' ? 'business' : 'private';
                            updatePurpose(entry.tripId, next);
                          }}
                          disabled={
                            rowLocked(entry) ||
                            isAdjRow(entry) ||
                            updatingPurpose === entry.tripId
                          }
                          title={
                            isAdjRow(entry)
                              ? t('reports.drivingLog.adjustmentPurposeDisabledTitle')
                              : entry.purpose === 'private'
                                ? t('reports.drivingLog.togglePrivateTitle')
                                : entry.purpose === 'business'
                                  ? t('reports.drivingLog.toggleBusinessTitle')
                                  : t('reports.drivingLog.toggleUnsetTitle')
                          }
                          className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed ${
                            entry.purpose === 'private'
                              ? 'bg-green-500'
                              : entry.purpose === 'business'
                                ? 'bg-red-500'
                                : 'bg-gray-300'
                          }`}
                        >
                          <span
                            className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                              entry.purpose === 'private' ? 'translate-x-4' : 'translate-x-0'
                            }`}
                          />
                        </button>
                      </div>
                    </td>
                    <td className="px-2 py-1 align-top">
                      <textarea
                        rows={2}
                        value={entry.note ?? ''}
                        disabled={rowLocked(entry)}
                        onChange={(e) => {
                          const v = e.target.value;
                          setEntries((prev) => prev.map((x) => (x.tripId === entry.tripId ? { ...x, note: v } : x)));
                          scheduleNoteSave(entry.tripId, v);
                        }}
                        className="w-full min-w-[260px] max-w-xl text-xs px-2 py-1 border border-border rounded-md bg-bgBase text-textDefault disabled:opacity-60"
                        placeholder={t('reports.drivingLog.notePlaceholder')}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-bgContainer border-t-2 border-border print:hidden">
                <tr>
                  <td colSpan={6} className="px-2 py-2 text-right font-bold text-textPrimary text-sm">
                    {t('reports.drivingLog.footerSum')}:
                  </td>
                  <td className="px-2 py-2 text-right font-bold text-blue-600 text-sm whitespace-nowrap">
                    {totalDistance.toFixed(1)} km
                  </td>
                  <td colSpan={5} />
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
        </div>
      ) : (
        !isLoading &&
        selectedVehicleId && (
          <div className="bg-bgElevated rounded-lg border border-border p-12 text-center">
            <ClipboardDocumentListIcon className="w-16 h-16 text-textSecondary mx-auto mb-4" />
            <h3 className="text-lg font-medium text-textPrimary mb-2">{t('reports.drivingLog.emptyTitle')}</h3>
            <p className="text-textSecondary">{t('reports.drivingLog.emptyBody')}</p>
          </div>
        )
      )}

      <div className="mt-6 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 p-4">
        <h3 className="font-medium text-blue-800 dark:text-blue-300 mb-2">{t('reports.drivingLog.infoTitle')}</h3>
        <ul className="text-sm text-blue-700 dark:text-blue-400 space-y-1">
          <li>• {t('reports.drivingLog.infoLine1')}</li>
          <li>• {t('reports.drivingLog.infoLine2')}</li>
          <li>• {t('reports.drivingLog.infoLine3')}</li>
          <li>• {t('reports.drivingLog.infoLine4')}</li>
          <li>• {t('reports.drivingLog.infoLine5')}</li>
          <li>• {t('reports.drivingLog.infoLine6')}</li>
        </ul>
      </div>

      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .driving-log-print-root,
          .driving-log-print-root * {
            visibility: visible;
          }
          .driving-log-print-root {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            max-width: 100%;
          }
          .driving-log-print-root .bg-bgElevated,
          .driving-log-print-root .bg-bgContainer {
            background: white !important;
          }
          .driving-log-print-root .text-textPrimary,
          .driving-log-print-root .text-textSecondary {
            color: black !important;
          }
          .driving-log-print-root .text-blue-600 {
            color: #1d4ed8 !important;
          }
          .driving-log-print-root .text-amber-700,
          .driving-log-print-root .text-amber-800 {
            color: #92400e !important;
          }
          .driving-log-print-root .text-red-600,
          .driving-log-print-root .text-red-700 {
            color: #dc2626 !important;
          }
          .driving-log-print-root .text-green-600,
          .driving-log-print-root .text-emerald-700 {
            color: #16a34a !important;
          }
          .driving-log-print-root .border-border {
            border-color: #ccc !important;
          }
        }
      `}</style>
    </div>
  );
};

export default DrivingLog;
